"use client";

import { useState } from "react";
import { useI18n } from "@/i18n";
import { apiClient } from "@/lib/api/client";

const COMPANY = "GuineeMall";
const EMAIL = "18918855336@126.com";
const PHONE = "+86-21-37788331";
const WHATSAPP = "+86 18918515539 / 447598254056";
const ADDRESS = "Shanghai, China";

const styles = {
    page: {
        minHeight: "100vh",
        background: "#F5F7F5",
        paddingTop: 96,
        paddingBottom: 80,
    },
    container: {
        maxWidth: 960,
        margin: "0 auto",
        padding: "0 24px",
    },
    titleRow: {
        textAlign: "center",
        marginBottom: 32,
    },
    title: {
        fontSize: 32,
        fontWeight: 800,
        color: "#111827",
        margin: 0,
        letterSpacing: "-0.01em",
    },
    subtitle: {
        fontSize: 14,
        color: "#6B7280",
        marginTop: 8,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "340px 1fr",
        gap: 24,
    },
    leftCol: {
        display: "flex",
        flexDirection: "column",
        gap: 12,
    },
    rightCol: {
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #ECEEEC",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        padding: "32px 36px",
    },
    contactCard: {
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #ECEEEC",
        padding: "18px 20px",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
    },
    contactIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        background: "#F0FDF4",
        color: "#2F9E44",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        flexShrink: 0,
    },
    contactLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 2,
    },
    contactValue: {
        fontSize: 15,
        fontWeight: 600,
        color: "#111827",
    },
    contactSub: {
        fontSize: 13,
        color: "#6B7280",
        marginTop: 2,
    },
    hoursCard: {
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #ECEEEC",
        padding: "18px 20px",
    },
    hoursTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 10,
    },
    hoursLine: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: 14,
        color: "#374151",
        padding: "4px 0",
    },
    formTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: "#111827",
        marginBottom: 4,
    },
    formSubtitle: {
        fontSize: 13.5,
        color: "#6B7280",
        marginBottom: 24,
    },
    field: { marginBottom: 16 },
    label: {
        display: "block",
        fontSize: 13,
        fontWeight: 600,
        color: "#374151",
        marginBottom: 6,
    },
    labelStar: {
        color: "#EF4444",
        marginLeft: 2,
    },
    input: {
        width: "100%",
        height: 44,
        padding: "0 14px",
        border: "1.5px solid #E5E7EB",
        borderRadius: 10,
        background: "#F9FAF9",
        fontSize: 14,
        outline: "none",
        transition: "all 0.2s ease",
        boxSizing: "border-box",
    },
    inputError: {
        border: "1.5px solid #EF4444",
        background: "#FFF7F7",
    },
    textarea: {
        width: "100%",
        height: 120,
        padding: "12px 14px",
        border: "1.5px solid #E5E7EB",
        borderRadius: 10,
        background: "#F9FAF9",
        fontSize: 14,
        outline: "none",
        fontFamily: "inherit",
        resize: "vertical",
        transition: "all 0.2s ease",
        boxSizing: "border-box",
    },
    textareaError: {
        border: "1.5px solid #EF4444",
        background: "#FFF7F7",
    },
    errorMsg: {
        fontSize: 12,
        color: "#EF4444",
        marginTop: 4,
        lineHeight: 1.4,
    },
    row2: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
    },
    btnSubmit: {
        width: "100%",
        height: 46,
        background: "#2F9E44",
        color: "#fff",
        border: "none",
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        marginTop: 6,
        transition: "opacity 0.18s ease",
    },
    btnDisabled: { opacity: 0.6, cursor: "not-allowed" },
    toast: {
        position: "fixed",
        top: 20,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "12px 24px",
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 500,
        zIndex: 9999,
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
    },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
    const { t } = useI18n();
    const [form, setForm] = useState({ name: "", email: "", contact: "", subject: "", message: "" });
    const [errors, setErrors] = useState({ name: "", email: "", contact: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (message, type) => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 2800);
    };

    const update = (k) => (e) => {
        const v = e.target.value;
        setForm((p) => ({ ...p, [k]: v }));
        // 用户输入时清除该字段错误
        setErrors((prev) => (prev[k] ? { ...prev, [k]: "" } : prev));
    };

    // 校验：返回 errors 对象
    const validateForm = () => {
        const e = {};
        if (!form.name.trim()) e.name = t("contact.errName");
        if (!EMAIL_RE.test(form.email.trim())) e.email = t("contact.errEmail");
        if (!form.contact.trim()) e.contact = t("contact.errContact");
        if (!form.subject.trim()) e.subject = t("contact.errSubject");
        if (!form.message.trim()) e.message = t("contact.errMessage");
        return e;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        const eMap = validateForm();
        setErrors(eMap);

        // 有任何错误就中止
        if (Object.keys(eMap).length > 0) return;

        setSubmitting(true);
        try {
            await apiClient.post("/feedback", {
                type: "message",
                name: form.name.trim(),
                email: form.email.trim(),
                subject: form.subject.trim(),
                content: form.message.trim(),
                contact: form.contact.trim(),
            });
            showToast(t("contact.submitSuccess"), "success");
            setForm({ name: "", email: "", contact: "", subject: "", message: "" });
            setErrors({ name: "", email: "", contact: "", subject: "", message: "" });
        } catch (err) {
            showToast(t("contact.submitFail"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    // 渲染 label（带红星）
    const Label = ({ i18nKey }) => (
        <label style={styles.label}>
            {t(i18nKey)}
            <span style={styles.labelStar}>*</span>
        </label>
    );

    return (
        <div style={styles.page}>
            {toast && (
                <div
                    style={{
                        ...styles.toast,
                        background: toast.type === "success" ? "#111827" : "#EF4444",
                        color: "#fff",
                    }}
                >
                    {toast.message}
                </div>
            )}
            <div style={styles.container}>
                <div style={styles.titleRow}>
                    <h1 style={styles.title}>{t("contact.title")}</h1>
                    <div style={styles.subtitle}>{t("contact.subtitle")}</div>
                </div>

                <div style={styles.grid}>
                    {/* Left contact info */}
                    <div style={styles.leftCol}>
                        <div style={styles.contactCard}>
                            <div style={styles.contactIcon}>✉️</div>
                            <div>
                                <div style={styles.contactLabel}>{t("contact.email")}</div>
                                <a
                                    href={`mailto:${EMAIL}`}
                                    style={{ ...styles.contactValue, color: "#2F9E44", textDecoration: "none" }}
                                >
                                    {EMAIL}
                                </a>
                                <div style={styles.contactSub}>{t("contact.emailDesc")}</div>
                            </div>
                        </div>

                        <div style={styles.contactCard}>
                            <div style={styles.contactIcon}>📞</div>
                            <div>
                                <div style={styles.contactLabel}>{t("contact.phone")}</div>
                                <div style={styles.contactValue}>{PHONE}</div>
                                <div style={styles.contactSub}>{t("contact.phoneDesc")}</div>
                            </div>
                        </div>

                        <div style={styles.contactCard}>
                            <div style={styles.contactIcon}>💬</div>
                            <div>
                                <div style={styles.contactLabel}>{t("contact.whatsapp")}</div>
                                <div style={styles.contactValue}>{WHATSAPP}</div>
                                <div style={styles.contactSub}>{t("contact.whatsappDesc")}</div>
                            </div>
                        </div>

                        <div style={styles.contactCard}>
                            <div style={styles.contactIcon}>📍</div>
                            <div>
                                <div style={styles.contactLabel}>{t("contact.address")}</div>
                                <div style={styles.contactValue}>{ADDRESS}</div>
                                <div style={styles.contactSub}>{t("contact.addressDesc")}</div>
                            </div>
                        </div>

                        <div style={styles.hoursCard}>
                            <div style={styles.hoursTitle}>🕐 {t("contact.hoursTitle")}</div>
                            <div style={styles.hoursLine}>
                                <span>{t("contact.weekday")}</span>
                                <span style={{ fontWeight: 600 }}>09:00 – 18:00</span>
                            </div>
                            <div style={styles.hoursLine}>
                                <span>{t("contact.saturday")}</span>
                                <span style={{ fontWeight: 600 }}>09:00 – 14:00</span>
                            </div>
                            <div style={{ ...styles.hoursLine, color: "#9CA3AF" }}>
                                <span>{t("contact.closed")}</span>
                                <span>—</span>
                            </div>
                        </div>
                    </div>

                    {/* Right form */}
                    <div style={styles.rightCol}>
                        <div style={styles.formTitle}>{t("contact.formTitle")}</div>
                        <div style={styles.formSubtitle}>{t("contact.formDesc")}</div>

                        <form onSubmit={onSubmit} noValidate>
                            <div style={styles.row2}>
                                <div style={styles.field}>
                                    <Label i18nKey="contact.name" />
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder={t("contact.name")}
                                        style={{ ...styles.input, ...(errors.name ? styles.inputError : {}) }}
                                    />
                                    {errors.name && <div style={styles.errorMsg}>{errors.name}</div>}
                                </div>
                                <div style={styles.field}>
                                    <Label i18nKey="contact.email" />
                                    <input
                                        type="text"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="example@email.com"
                                        style={{ ...styles.input, ...(errors.email ? styles.inputError : {}) }}
                                    />
                                    {errors.email && <div style={styles.errorMsg}>{errors.email}</div>}
                                </div>
                            </div>

                            <div style={styles.field}>
                                <Label i18nKey="contact.contact" />
                                <input
                                    type="text"
                                    value={form.contact}
                                    onChange={update("contact")}
                                    placeholder={t("contact.contactPlaceholder")}
                                    style={{ ...styles.input, ...(errors.contact ? styles.inputError : {}) }}
                                />
                                {errors.contact && <div style={styles.errorMsg}>{errors.contact}</div>}
                            </div>

                            <div style={styles.field}>
                                <Label i18nKey="contact.subject" />
                                <input
                                    type="text"
                                    value={form.subject}
                                    onChange={update("subject")}
                                    placeholder={t("contact.subject")}
                                    style={{ ...styles.input, ...(errors.subject ? styles.inputError : {}) }}
                                />
                                {errors.subject && <div style={styles.errorMsg}>{errors.subject}</div>}
                            </div>

                            <div style={styles.field}>
                                <Label i18nKey="contact.message" />
                                <textarea
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder={t("contact.messagePlaceholder")}
                                    style={{ ...styles.textarea, ...(errors.message ? styles.textareaError : {}) }}
                                />
                                {errors.message && <div style={styles.errorMsg}>{errors.message}</div>}
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                style={{
                                    ...styles.btnSubmit,
                                    ...(submitting ? styles.btnDisabled : {}),
                                }}
                            >
                                {submitting ? t("contact.sending") : t("contact.send")}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
